import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** Width of the right-hand control column. Keep in sync with `right-[100px]` on `.capture-stage`. */
export const CAPTURE_RAIL_WIDTH_PX = 100;

const shutterToneClass = {
  green: 'bg-green-500 hover:bg-green-600',
  red: 'bg-red-500 hover:bg-red-600',
  blue: 'bg-blue-600 hover:bg-blue-700',
} as const;

type CaptureSurface = 'dark' | 'light';

const railSurfaceClass: Record<CaptureSurface, string> = {
  dark: 'bg-black',
  light: 'bg-white border-l border-gray-300',
};

const shutterRingClass = 'border-gray-200 shadow-md';

interface CaptureActionRailProps {
  children: ReactNode;
  className?: string;
  surface?: CaptureSurface;
}

/**
 * 100px right-hand control column used by the step screen, illustration,
 * camera, and photo progress screen so the primary action stays in one place.
 */
export function CaptureActionRail({ children, className, surface = 'dark' }: CaptureActionRailProps) {
  return (
    <div
      data-testid="capture-action-rail"
      className={cn('w-[100px] shrink-0 relative h-full', railSurfaceClass[surface], className)}
    >
      {children}
    </div>
  );
}

interface CaptureShutterButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone: keyof typeof shutterToneClass;
  surface?: CaptureSurface;
}

export function CaptureShutterButton({
  tone,
  surface = 'dark',
  className,
  children,
  type = 'button',
  ...props
}: CaptureShutterButtonProps) {
  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
      <button
        type={type}
        className={cn(
          'w-20 h-20 rounded-full border-4 active:scale-95 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed',
          shutterRingClass,
          shutterToneClass[tone],
          className
        )}
        {...props}
      >
        {children}
      </button>
    </div>
  );
}

interface CaptureRailSecondaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Bottom is Back; top holds an extra control such as the torch. */
  placement?: 'top' | 'bottom';
}

export function CaptureRailSecondaryButton({
  placement = 'bottom',
  className,
  children,
  type = 'button',
  ...props
}: CaptureRailSecondaryButtonProps) {
  return (
    <div className={cn('absolute left-1/2 -translate-x-1/2', placement === 'top' ? 'top-6' : 'bottom-6')}>
      <button
        type={type}
        className={cn(
          'w-16 h-16 rounded-full bg-gray-900 border-4 border-gray-200 shadow-md active:scale-95 transition-transform flex items-center justify-center disabled:opacity-50',
          className
        )}
        {...props}
      >
        {children}
      </button>
    </div>
  );
}
